"use client";

import React, {
  useActionState,
  useEffect,
  useState,
  startTransition,
} from "react";
import { CMSData } from "@/cms/models";
import { ApplicationFraction } from "@/utils/gql/graphql";
import FormLayout from "../inputs/FormLayout";
import StoreSelect from "../inputs/StoreSelect";
import TextAreaInput from "../inputs/TextAreaInput";
import TextInput from "../inputs/TextInput";
import { submitContactForm } from "@/actions/contact-form";
import { CONTACT_INITIAL_STATE } from "@/services/form.service";
import ConfirmModal from "../ConfirmModal";
import { formatPhoneNumberObject } from "@/utils/helper";

export interface ContactFormProps {
  config: {
    cmsData: CMSData;
    app: ApplicationFraction;
    messageTextPre?: string;
    messageTextPost?: string;
    confirmDescription?: string;
    confirmTitle?: string;
    resourceGroupId?: string;
  };
  recaptchaSiteKey: string;
}

const ContactForm: React.FC<ContactFormProps> = ({
  config,
  recaptchaSiteKey,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isInvalidCaptchaOpen, setIsInvalidCaptchaOpen] = useState(false);
  const [formState, formAction, isPending] = useActionState(submitContactForm, {
    ...CONTACT_INITIAL_STATE,
    data: {
      ...CONTACT_INITIAL_STATE.data,
    },
  });

  const submit = (payload: FormData) => {
    (async () => {
      try {
        const token = await grecaptcha.enterprise.execute(recaptchaSiteKey, {
          action: "WishForm",
        });
        payload.append("recaptchaToken", token);
      } catch (error) {
        console.log("[ReCaptcha Error]:", error);
        payload.delete("recaptchaToken");
      } finally {
        startTransition(() => {
          formAction(payload);
        });
      }
    })();
  };

  useEffect(() => {
    if (!("recaptchaIsValid" in formState)) {
      return;
    }

    if (!formState.recaptchaIsValid) {
      setIsInvalidCaptchaOpen(true);
    } else {
      setIsOpen(formState.isSuccess ?? false);
    }
  }, [formState]);

  const { firstName, lastName, address, zip, city, phone, email, message } =
    formState?.data || {};

  const { confirmDescription, confirmTitle, resourceGroupId } = config;

  return (
    <div className="flex w-full flex-1">
      <ConfirmModal
        description={confirmDescription ?? "Du kan altid kontakte os på:"}
        title={confirmTitle ?? "Tak for din henvendelse"}
        setOpen={(isOpen) => setIsOpen(isOpen)}
        isOpen={isOpen}
        contactInfo={{
          phone: formatPhoneNumberObject(formState.contactInfo?.phone ?? ""),
          email: formState.contactInfo?.email?.address ?? "",
        }}
      />
      <ConfirmModal
        modalType="error"
        description="Prøv venligst igen. Hvis du fortsætter med at opleve denne fejl så kontakt venligst support."
        title="Din forespørgsel blev blokeret"
        setOpen={(isOpen) => setIsInvalidCaptchaOpen(isOpen)}
        isOpen={isInvalidCaptchaOpen}
      />
      <FormLayout
        submitLabel="Send"
        featureFlags={config?.cmsData?.featureFlags}
        isPending={isPending}
        formAction={submit}
      >
        <input
          type="hidden"
          name="messageTextPre"
          defaultValue={config.messageTextPre}
        />
        <input
          type="hidden"
          name="messageTextPost"
          defaultValue={config.messageTextPost}
        />

        {resourceGroupId ? (
          <input type="hidden" name="resourceGroupId" value={resourceGroupId} />
        ) : (
          <StoreSelect
            cmsData={config.cmsData}
            label="Vælg butik"
            name="resourceGroupId"
            stores={config.app.stores!}
          />
        )}
        <TextInput
          cmsData={config.cmsData}
          name="firstName"
          label="Fornavn"
          defaultValue={firstName}
          errors={formState?.zodErrors?.firstName}
          required={config.cmsData?.forms.nameRequired}
        />
        <TextInput
          cmsData={config.cmsData}
          name="lastName"
          label="Efternavn"
          defaultValue={lastName}
          errors={formState?.zodErrors?.lastName}
          required={config.cmsData?.forms.nameRequired}
        />
        <TextInput
          cmsData={config.cmsData}
          className="col-span-6"
          name="address"
          label="Adresse"
          defaultValue={address}
          errors={formState?.zodErrors?.address}
          required={config.cmsData?.forms.addressRequired}
        />
        <TextInput
          cmsData={config.cmsData}
          className="col-span-2"
          name="zip"
          label="Postnr"
          defaultValue={zip}
          errors={formState?.zodErrors?.zip}
          required={config.cmsData?.forms.zipRequired}
        />
        <TextInput
          cmsData={config.cmsData}
          className="col-span-4"
          name="city"
          label="By"
          defaultValue={city}
          errors={formState?.zodErrors?.city}
          required={config.cmsData?.forms.cityRequired}
        />
        <TextInput
          cmsData={config.cmsData}
          className="col-span-3"
          name="phone"
          label="Telefonnummer"
          defaultValue={phone}
          errors={formState?.zodErrors?.phone}
          required={config.cmsData?.forms.phoneRequired}
        />
        <TextInput
          cmsData={config.cmsData}
          className="col-span-3"
          name="email"
          label="E-mail"
          defaultValue={email}
          errors={formState?.zodErrors?.email}
          required={config.cmsData?.forms.emailRequired}
        />
        <TextAreaInput
          cmsData={config.cmsData}
          name="message"
          label="Besked"
          defaultValue={message}
          errors={formState?.zodErrors?.message}
          required={false}
        />
      </FormLayout>
    </div>
  );
};

export default ContactForm;
